import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

import {
  downloadStatementCsv,
  downloadStatementPdf,
  fetchStatementData,
  listMyBillingProfiles,
  listStatementOptions,
} from '@src/api/myBilling';
import { useAuth } from '@src/context';

const money = (v) =>
  Number(v || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function MyStatementsPage() {
  const { state: authState } = useAuth();
  const isStaff =
    authState?.user?.is_staff || authState?.user?.is_superuser;

  // null = still resolving the visibility flag (client-only gate).
  const [uiEnabled, setUiEnabled] = useState(null);
  const [clients, setClients] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [clientId, setClientId] = useState('');
  const [period, setPeriod] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Client-facing visibility gate (staff bypass — same rule as the other
  // client billing pages).
  useEffect(() => {
    let alive = true;
    listMyBillingProfiles()
      .then((resp) => {
        if (!alive) return;
        const d = resp?.data;
        const rows = Array.isArray(d) ? d : d?.results || [];
        setUiEnabled(rows.some((p) => p.client_billing_ui_enabled));
      })
      .catch(() => {
        if (alive) setUiEnabled(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    listStatementOptions()
      .then((resp) => {
        if (!alive) return;
        const d = resp?.data || {};
        const cs = d.clients || [];
        const ps = d.periods || [];
        setClients(cs);
        setPeriods(ps);
        if (cs.length) setClientId(String(cs[0].client_id));
        if (ps.length) setPeriod(ps[0].value);
      })
      .catch((err) => {
        if (alive)
          setError(
            err?.response?.data?.detail || err?.message || 'Failed to load.',
          );
      });
    return () => {
      alive = false;
    };
  }, []);

  const queryParams = useMemo(() => {
    const p = {};
    if (clientId) p.client_id = clientId;
    if (period) p.period = period;
    return p;
  }, [clientId, period]);

  const load = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchStatementData(queryParams);
      setData(resp?.data || null);
    } catch (err) {
      setData(null);
      setError(
        err?.response?.data?.detail || err?.message || 'No statement found.',
      );
    } finally {
      setLoading(false);
    }
  }, [queryParams, period]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownload = async (fmt) => {
    setBusy(true);
    try {
      const fn = fmt === 'pdf' ? downloadStatementPdf : downloadStatementCsv;
      const blob = await fn(queryParams);
      const name = data?.client_name
        ? data.client_name.replace(/\s+/g, '_')
        : 'client';
      triggerBlobDownload(blob, `spend-statement-${name}-${period}.${fmt}`);
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || 'Download failed.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (!isStaff && uiEnabled === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isStaff && !uiEnabled) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          Your billing is managed directly by your Lead Gurus account
          manager. Please reach out to them with any billing questions.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5">Monthly Statements</Typography>
        <Typography variant="body2" color="text.secondary">
          Ad spend by territory &amp; market, with management fee. For your
          records — not a bill.
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        {(isStaff || clients.length > 1) && (
          <TextField
            label="Client"
            select
            size="small"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {clients.map((c) => (
              <MenuItem key={c.client_id} value={String(c.client_id)}>
                {c.client_name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          label="Month"
          select
          size="small"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {periods.map((p) => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          startIcon={<DownloadIcon />}
          disabled={busy || !data}
          onClick={() => handleDownload('pdf')}
        >
          PDF
        </Button>
        <Button
          startIcon={<DownloadIcon />}
          disabled={busy || !data}
          onClick={() => handleDownload('csv')}
        >
          CSV
        </Button>
      </Stack>

      {error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {data.client_name} — {data.period_label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ad spend {money(data.base_spend)} + management fee{' '}
                {money(data.fee_amount)} ({data.fee_percentage}%) ={' '}
                <strong>{money(data.total)}</strong>
              </Typography>
            </CardContent>
          </Card>

          <TableContainer component={Card} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Territory</TableCell>
                  <TableCell>Market</TableCell>
                  <TableCell align="right">Ad spend</TableCell>
                  <TableCell align="right">Mgmt fee</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.breakdown.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No recorded spend for this month.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {data.breakdown.map((r, i) => (
                  <TableRow key={`${r.territory}-${r.market}-${i}`}>
                    <TableCell>{r.territory}</TableCell>
                    <TableCell>{r.market}</TableCell>
                    <TableCell align="right">{money(r.base_spend)}</TableCell>
                    <TableCell align="right">{money(r.fee_amount)}</TableCell>
                    <TableCell align="right">{money(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </Container>
  );
}
